import { useEffect, useState } from "react";

/**
 * The developer pages' theme: DARK by default, light if asked for, remembered.
 *
 * Extracted the moment there was a second developer page, rather than copied
 * into it. The rule "dark unless a stored choice says light" is a decision, and
 * a decision that exists in two files is a decision that will eventually be two
 * different decisions — one page defaulting dark and the other following the
 * system, with nothing to show which was intended.
 *
 * It deliberately does NOT read prefers-color-scheme. Reading it would mean a
 * machine set to light never sees the intended default, which is the opposite of
 * "dark unless I change it". The theme is resolved to a concrete value here and
 * written to a class, so no colour has to be defined inside a media query.
 *
 * Every storage access is wrapped: a browser with site data blocked throws on
 * read, and a page that cannot remember a preference should still render.
 */
const THEME_KEY = "dev-theme";

export default function useDevTheme() {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === "dark" || saved === "light") return saved;
    } catch {
      /* storage blocked — fall through to the default */
    }
    return "dark";
  });

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* remembering the choice is a convenience, not a requirement */
    }
  }, [theme]);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return { theme, setTheme, toggle, dark: theme === "dark" };
}
